import React, { useState, useEffect, ReactElement } from "react";

// modules
import { useSelector, shallowEqual } from "react-redux";
import Countdown from "react-countdown";

// Components
import PollsLayout from "../../components/polls/polls.layout";
import VideoStream from "../../components/videoStream/videoStream";
import Image from "../../components/image/image.component";

//types
import { PollRatingType, PollWordCloudType, PollMCQsType } from "../../types";

// Styles
import "./event.component.scss";

// Images
import remoteLogoWhite from "../../assets/images/remote-meeting-logo-white.png";

// apis
import {
  auth,
  fetchQuestions,
  fetchStreamInfoById,
  onSnapshotSessionQuestion,
} from "../../apis/firebase";

import { useTranslation } from "react-i18next";

import {
  getHourAndMintFromFBTimeStamp,
  getDateAndTimeFromFBTimeStamp,
} from "../../utils/helpers";
import { Link } from "react-router-dom";

let sessionQuestionRef = null;

const EventPage = (): ReactElement => {
  const [currentPoll, setCurrentPoll] = useState<
    PollRatingType | PollWordCloudType | PollMCQsType | any
  >();

  const {
    track,
    generalSettings,
    session: activeSession,
    poll: activePoll,
  } = useSelector(
    ({ generalSettings, session, poll, track }) => ({
      generalSettings,
      session,
      poll,
      track,
    }),
    shallowEqual
  );

  const [sessionDetails, setSessionDetails] = useState<any>(null);

  const [streamDetail, setStreamDetail] = useState<any>(null);
  const [sessionName, setSessionName] = useState<string>(null);

  const [roomSwitch, setRoomSwitch] = useState<boolean>(false);

  const [questionList, setQuestionList] = useState([]);

  const [tabTypes, setTabTypes] = useState("");

  const { t } = useTranslation();

  useEffect(() => {
    if (!activeSession?.docId) {
      setSessionDetails(null);
      setStreamDetail(null);
      setSessionName("");
      return;
    }
    setSessionDetails({ ...activeSession });
  }, [activeSession.docId]);

  useEffect(() => {
    if (!activePoll.docId || !track.docId) {
      setCurrentPoll(null);
      return;
    }

    const _poll = { ...activePoll };
    _poll.pollId = activePoll.docId;
    _poll.trackId = track.docId;
    _poll.sessionId = activeSession.docId;
    setCurrentPoll({ ..._poll });
  }, [activePoll, track]);

  useEffect(() => {
    if (!!currentPoll) {
      setTabTypes("polls");
    } else if (tabTypes === "polls") {
      if (!roomSwitch) {
        setTabTypes("");
      } else {
        setTabTypes("question");
      }
    }
  }, [currentPoll]);

  useEffect(() => {
    if (activeSession?.streams && activeSession.streams.length) {
      const _activeStream = activeSession?.streams?.find((d) => d.isActive);
      if (_activeStream) {
        fetchSteamDetails(_activeStream);
      } else {
        setStreamDetail(null);
      }
    } else {
      setStreamDetail(null);
    }
  }, [activeSession?.streams]);

  useEffect(() => {
    if (!sessionDetails || !sessionDetails.docId) return;

    if (sessionDetails?.streams && sessionDetails.streams.length) {
      const _activeStream = sessionDetails?.streams?.find((d) => d.isActive);

      if (_activeStream) {
        fetchSteamDetails(_activeStream);
      }
    }

    if (sessionDetails?.name) {
      setSessionName(sessionDetails?.name);
    }

    (async () => {
      sessionQuestionRef = onSnapshotSessionQuestion(
        sessionDetails.docId,
        await auth.currentUser.uid,
        sessionDetails?.qnaModeration,
        (data) => {
          setQuestionList([...data]);
        }
      );
    })();

    return () => sessionQuestionRef();
  }, [sessionDetails?.docId]);

  const fetchSteamDetails = async ({ streamId }) => {
    if (streamId) {
      const _detail = await fetchStreamInfoById(streamId);

      setStreamDetail(_detail);
    } else {
      setStreamDetail(null);
    }
  };

  const getTabTitle = () => {
    if (tabTypes === "question") {
      return t("LIVE_NAV_QNA");
    }
    if (tabTypes === "polls") {
      return t("LIVE_NAV_POLLS");
    }
    if (tabTypes === "playlist") {
      return t("LIVE_NAV_PLAYLIST");
    }
  };

  return (
    <>
      {!!activeSession?.docId && (
        <div className="tm__event-wrapper d-flex flex-column h-100 w-100">
          <div className="d-flex flex-column flex-1 h-100 mh-0">
            <div
              className={`d-flex justify-content-center flex-1 h-100 mh-0
            ${roomSwitch ? "tm__out-of-room" : ""}
          `}>
              {!roomSwitch && (
                <div className="tm__event-player flex-1">
                  <VideoStream
                    streamDetail={streamDetail}
                    stream_id={streamDetail?.settings?.videoId}
                  />
                </div>
              )}
              <div
                className={`d-flex flex-column tm__event-polls
              ${tabTypes ? "tm__event-polls-active" : ""}
            `}>
                <div className="tm__event-polls-head d-flex align-items-center justify-content-between p-2">
                  <label className="fw-bold text-capitalize">
                    {getTabTitle()}
                  </label>
                  {!roomSwitch && (
                    <button
                      type="button"
                      onClick={() => {
                        setTabTypes("");
                      }}>
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
                <div
                  className={`tm__event-polls-body flex-1
                ${tabTypes === "polls" ? "p-4" : ""}
              `}>
                  {tabTypes && tabTypes === "question" && (
                    <PollsLayout
                      type="question"
                      questions={questionList}
                      sessionId={sessionDetails.docId}
                      sessionDetails={sessionDetails}></PollsLayout>
                  )}
                  {tabTypes && tabTypes === "polls" && (
                    <PollsLayout {...currentPoll}></PollsLayout>
                  )}
                  {tabTypes && tabTypes === "playlist" && (
                    <div className="m-2">
                      {track?.sessions &&
                        track?.sessions.map((d, i) => (
                          <div
                            key={d.docId}
                            className={`tm__polls
                        ${d.isActive ? "tm__active-playlist" : "opacity-50"}
                      `}>
                            {/* upcoming-playlist class for upcoming playlist */}
                            <div className="tm__polls-body d-flex">
                              <div className="d-flex flex-column w-100">
                                <label className="fs-7 m-0 lh-sm text-primary mb-2">
                                  {d.name}
                                </label>
                                <div className="d-flex justify-content-between align-items-center lh-sm">
                                  <div className="d-flex align-items-center">
                                    <i className="bi bi-clock-fill fs-9 me-1"></i>
                                    <small className="fs-9 lh-sm">
                                      {d.startTime &&
                                        getDateAndTimeFromFBTimeStamp(
                                          d.startTime.seconds
                                        )}
                                      {" - "}
                                      {d.finishTime &&
                                        getHourAndMintFromFBTimeStamp(
                                          d.finishTime.seconds
                                        )}
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                      <div className="tm__polls-body"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-3 text-center text-white">
              <label className="fs-5">{sessionName}</label>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-center justify-content-lg-between">
            <div className="tm__event-logos mw-15 d-none d-lg-block">
              <img src={remoteLogoWhite} alt="" className="h-100" />
            </div>
            <div className="tm__event-navbar-wrapper d-flex">
              <div className="tm__room-switch d-flex">
                {generalSettings?.liveNavigation?.userLocationEnabled && (
                  <>
                    <ul className="list-style-none d-flex flex-column align-items-end justify-content-between m-0 p-0">
                      <li className="d-flex align-items-center">
                        {t("LIVE_NAV_USER_LOCATION_PHYSICAL")}
                      </li>
                      <li className="d-flex align-items-center">
                        {t("LIVE_NAV_USER_LOCATION_REMOTE")}
                      </li>
                    </ul>

                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        onChange={(e) => {
                          setRoomSwitch(e.target.checked);
                          setTabTypes("question");
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
              <nav id="tm__event-navbar">
                <ul className="d-flex align-items-center list-style-none m-0 p-0">
                  {generalSettings?.liveNavigation?.homeEnabled &&
                    generalSettings?.micrositeEnabled && (
                      <li>
                        <Link
                          to="/"
                          className="d-flex flex-column align-items-center">
                          <i className="bi bi-house-fill"></i>
                          <label className="fs-8"> {t("LIVE_NAV_HOME")}</label>
                        </Link>
                      </li>
                    )}
                  {generalSettings?.liveNavigation?.qnaEnabled && (
                    <li>
                      <button
                        className={` d-flex flex-column align-items-center
                    ${tabTypes === "question" ? "active" : ""}
                  `}
                        onClick={() => {
                          setTabTypes(
                            tabTypes === "question" && !roomSwitch
                              ? ""
                              : "question"
                          );
                        }}>
                        <i className="bi bi-question-circle-fill"></i>
                        <label className="fs-8">{t("LIVE_NAV_QNA")}</label>
                      </button>
                    </li>
                  )}

                  {generalSettings?.liveNavigation?.pollsEnabled && (
                    <li>
                      <button
                        className={`d-flex flex-column align-items-center
                    ${tabTypes === "polls" ? "active" : ""}
                  `}
                        onClick={() => {
                          setTabTypes(
                            tabTypes === "polls" && !roomSwitch ? "" : "polls"
                          );
                        }}>
                        <i className="bi bi-bar-chart-line-fill"></i>
                        <label className="fs-8">{t("LIVE_NAV_POLLS")}</label>
                      </button>
                    </li>
                  )}

                  {generalSettings?.liveNavigation?.playlistEnabled && (
                    <li>
                      <button
                        className={`d-flex flex-column align-items-center
                    ${tabTypes === "playlist" ? "active" : ""}
                  `}
                        onClick={() => {
                          setTabTypes(
                            tabTypes === "playlist" && !roomSwitch
                              ? ""
                              : "playlist"
                          );
                        }}>
                        <i className="bi bi-collection-play-fill"></i>
                        <label className="fs-8">{t("LIVE_NAV_PLAYLIST")}</label>
                      </button>
                    </li>
                  )}
                </ul>
              </nav>
            </div>
            <div className="tm__event-logos mw-15 text-end d-none d-lg-block">
              <Image
                title={generalSettings?.clientName}
                path={
                  generalSettings?.clientLiveLogo || generalSettings?.clientLogo
                }
                className="h-100"
              />
            </div>
          </div>
        </div>
      )}
      {!activeSession?.docId && (
        <div className="d-flex align-items-center justify-content-center h-100 w-100">
          <div className="tm__event-coming-soon d-flex flex-column align-items-center text-center">
            <a className="mb-4" href="index.html">
              <Image
                title={generalSettings?.clientName}
                path={generalSettings?.clientLogo}
                height="100"
              />
            </a>
            {track?.sessions && track?.sessions.find((d) => !d.isFinished) && (
              <h1 className="fw-bold mb-4">
                {t("LIVE_HOLDING_STARTING_SOON")}
              </h1>
            )}

            {track?.sessions && !track?.sessions.find((d) => !d.isFinished) && (
              <>
                <h1 className="fw-bold mb-4">
                  {t("LIVE_HOLDING_EVENT_FINISHED")}
                </h1>

                {track.sessions.find(
                  (d) => !!d.replay && !!d.replay.length
                ) && (
                  <div className="mt-4">
                    <Link
                      className="btn btn-primary fw-bold fw-100"
                      to={`/replay`}>
                      {t("LIVE_HOLDING_WATCH_REPLAY")}
                    </Link>
                  </div>
                )}
              </>
            )}

            {track?.sessions && track?.sessions.find((d) => !d.isFinished) && (
              <div className="mb-2">
                <Countdown
                  date={
                    new Date(
                      track?.sessions.find((d) => !d.isFinished).startTime
                        .seconds * 1000
                    )
                  }
                  renderer={({ days, hours, minutes, seconds, completed }) => {
                    return (
                      <>
                        <div className="tm__countdown">
                          {!completed && (
                            <ul className="d-flex align-items-center m-0 p-0">
                              <li>
                                <span>{days}</span>
                                <small>{t("TEXT_DAYS")}</small>
                              </li>
                              <li>
                                <span>{hours}</span>
                                <small>{t("TEXT_HOURS")}</small>
                              </li>
                              <li>
                                <span>{minutes}</span>
                                <small>{t("TEXT_MINUTES")}</small>
                              </li>
                              <li>
                                <span>{seconds}</span>
                                <small>{t("TEXT_SECONDS")}</small>
                              </li>
                            </ul>
                          )}
                        </div>
                      </>
                    );
                  }}>
                  <></>
                </Countdown>
              </div>
            )}

            {generalSettings.micrositeEnabled && (
              <div className="mt-4">
                <Link className="btn btn-secondary fw-bold fw-100" to={`/`}>
                  {t("LIVE_NAV_HOME")}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default EventPage;
